import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import { classNames, generateMonths, groupEventsByDate } from "./calenderUtilties";
import DoubleMeetingEvents from "./DoubleMeetingEvents";


const Maanden = ({ aantal = 2, appointments, selectedDate, onSelectDate }) => {
  const [currentMonth, setCurrentMonth] = useState(() => moment());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    if (media.addEventListener) {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  /**
   * 1️⃣ Pre-group events once
   */
  const eventsByDate = useMemo(() => {
    return groupEventsByDate(appointments);
  }, [appointments]);

  /**
   * 2️⃣ Generate months ONLY when inputs change
   */
  const months = useMemo(() => {
    return generateMonths(currentMonth, isMobile ? 1 : aantal, eventsByDate);
  }, [currentMonth, aantal, eventsByDate, isMobile]);

  const prevMonth = () =>
    setCurrentMonth(m => m.clone().subtract(1, "month"));

  const nextMonth = () =>
    setCurrentMonth(m => m.clone().add(1, "month"));

  return (
    <div className="relative rounded-2xl border border-wa-border bg-wa-panel p-3 sm:p-4">
      <button
        type="button"
        onClick={prevMonth}
        className="absolute left-1 top-1 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg text-wa-muted transition hover:bg-wa-hover hover:text-wa-text sm:left-2 sm:top-2"
      >
        <span className="sr-only">Previous month</span>
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={nextMonth}
        className="absolute right-1 top-1 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg text-wa-muted transition hover:bg-wa-hover hover:text-wa-text sm:right-2 sm:top-2"
      >
        <span className="sr-only">Next month</span>
        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8">
        {months.map((month, monthIdx) => {
          const firstDayOfMonth = moment(month.days[0].date).startOf("week");
          const startingDayIndex = firstDayOfMonth.day();
          const numCols = 7;

          return (
            <section key={monthIdx} className="text-center">
              <h2 className="text-xl font-semibold capitalize text-wa-text">
                {month.name}
              </h2>
              <div className="mt-4 grid grid-cols-7 text-xs leading-6 text-wa-muted sm:text-sm">
                {Array.from({ length: numCols }).map((_, dayIdx) => (
                  <div key={dayIdx}>
                    <span className="sm:hidden">
                      {moment().day((dayIdx + startingDayIndex) % numCols).format("dd")}
                    </span>
                    <span className="hidden sm:inline">
                      {moment().day((dayIdx + startingDayIndex) % numCols).format("ddd")}
                    </span>
                  </div>
                ))}
              </div>
              <div className="isolate mt-2 grid grid-cols-7 gap-px overflow-hidden rounded-xl bg-wa-border text-sm shadow ring-1 ring-wa-border/80">
                {month.days.map((day, dayIdx) => {
                  const isSelected = selectedDate === day.date;
                  return (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => onSelectDate?.(day.date)}
                    className={classNames(
                      day.isCurrentMonth ? "bg-wa-panel2 text-wa-text" : "bg-wa-panel text-wa-muted",
                      isSelected && "font-semibold",
                      dayIdx === 0 && "rounded-tl-xl",
                      dayIdx === 6 && "rounded-tr-xl",
                      dayIdx === month.days.length - 7 && "rounded-bl-xl",
                      dayIdx === month.days.length - 1 && "rounded-br-xl",
                      "relative min-w-0 py-2 transition hover:bg-wa-hover focus:z-10 sm:py-2.5"
                    )}
                  >
                    <time
                      dateTime={day.date}
                      className={classNames(
                        "mx-auto flex h-7 w-7 items-center justify-center rounded-full text-sm",
                        day.isToday ? "bg-wa-accent text-wa-onaccent" : "",
                        isSelected && !day.isToday && "bg-wa-accent text-wa-onaccent",
                        day?.events?.length > 0 && !day.isToday && !isSelected ? "bg-wa-accent/20 text-wa-text" : ""
                      )}
                    >
                      {day.date.split("-").pop().replace(/^0/, "")}
                    </time>
                  </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      {isMobile ? (
        <p className="mt-3 text-center text-xs text-wa-muted">
          Navigatie links/rechts toont vorige of volgende maand.
        </p>
      ) : null}
    </div>
  );
}

const DoubleMeeting = ({ appointments = [] }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const events = useMemo(() => {
    if (!selectedDate) return appointments;
    return appointments.filter(
      (item) => moment(item?.start_time).format("YYYY-MM-DD") === selectedDate
    );
  }, [appointments, selectedDate]);

  const toggleDate = (date) => {
    setSelectedDate((prev) => (prev === date ? null : date));
  };

  return (
    <div className="space-y-4">
      <Maanden
        aantal={2}
        appointments={appointments}
        selectedDate={selectedDate}
        onSelectDate={toggleDate}
      />
      <div className="rounded-2xl border border-wa-border bg-wa-panel px-3 sm:px-4">
        <DoubleMeetingEvents events={events} selectedDate={selectedDate} />
      </div>
    </div>
  );
};

export default DoubleMeeting;
